import { useEffect, useRef } from 'react';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import { getEditionForProject } from '@jira/platform__application-editions/src/edition.tsx';
import { useIsTrelloCrossflowEnabled } from '@atlassian/jira-growth-utils/src/services/trello-crossflow/index.tsx';
import {
	useCommonEligibilityConditionsForJtbdOnboarding,
	useTailoredOnboardingPersonalisationM2,
} from '@atlassian/jira-onboarding-core/src/controllers/use-fetch-onboarding-personalisation/index.tsx';
import {
	PROJECT_SETTING_PAGES,
	QUICKSTART_UI_STATUS,
} from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';
import {
	useJswOnboarding,
	useQuickstartUiStatus,
} from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';

import { useConvertToRootTaskList } from '@atlassian/jira-onboarding-quickstart-core/src/utils/create-task-list/index.tsx';
import {
	useNavigationEditPlansPermission,
	useIsAdvancedRoadmapsTrial,
} from '@atlassian/jira-plans-item-visibility/src/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIsTargetedForReverseTrials } from '@atlassian/jira-reverse-trial-utils/src/controllers/use-is-targeted/index.tsx';
import type { ProjectContext as ProjectContextType } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { PEOPLE_SETTINGS_URL } from '@atlassian/jira-servicedesk-onboarding-checklist/src/common/constants.tsx';
import { extractTrailingSlashFromPath } from '@atlassian/jira-servicedesk-onboarding-checklist/src/common/utils.tsx';
import { BUSINESS } from '@atlassian/jira-servicedesk-onboarding-checklist/src/services/get-onboarding-type/constants.tsx';
import { useJsmOnboardingType } from '@atlassian/jira-servicedesk-onboarding-checklist/src/services/get-onboarding-type/main.tsx';
import { useHasOpened } from '@atlassian/jira-software-onboarding-quickstart-has-opened/src/services/has-opened/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsSampleProjectService } from '../../services/is-sample-project/index.tsx';
import { useRefreshOnJSMProjectSwitch } from '../refresh-on-jsm-project-switch/index.tsx';
import { useRefreshQuickstartOnProjectSwitch } from '../refresh-quickstart-on-project-switch/index.tsx';
import { useQuickstart } from '../should-show-experience/index.tsx';
import { getUserRole, getCanInvitePeopleToProject } from './utils.tsx';

/**
 * useQuickstartInitialise version calls refreshAppState only once
 * useQuickstartInitialiseWithPersonalisation calls refreshAppState or refreshAppStateUiStatusOnly on each projectType change
 *
 */
export const useQuickstartInitialise = (
	projectContextData: ProjectContextType | null,
	containerType: 'project' | 'plan' = 'project',
) => {
	const tenantContext = useTenantContext();
	const isTrelloCrossflow = useIsTrelloCrossflowEnabled();

	const canShowExperience = useQuickstart(projectContextData, containerType);
	const [quickstartStatus] = useQuickstartUiStatus();
	const [{ data: jsmOnboardingData }, { fetchOnboardingType }] = useJsmOnboardingType();
	useRefreshOnJSMProjectSwitch(projectContextData?.projectId, projectContextData?.projectType);

	const canEditPlans = useNavigationEditPlansPermission();
	const isAdvancedRoadmapsTrial = useIsAdvancedRoadmapsTrial();
	const isTargetedForReverseTrials = useIsTargetedForReverseTrials();

	const [
		{ isQuickstartLoaded, shouldRenderComponent },
		{
			refreshAppState,
			setShouldRenderComponent,
			forceHideNudge,
			setProjectState,
			syncVisibilityWithLocal,
			setContainerType,
		},
	] = useJswOnboarding();
	const [{ hasOpened }, { setHasOpened }] = useHasOpened();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isSampleProject, { fetchIsSampleProject }] = useIsSampleProjectService();

	const canInvitePeopleToProject = getCanInvitePeopleToProject(projectContextData, tenantContext);
	const route = useCurrentRoute();
	const isProjectSettingsPage = PROJECT_SETTING_PAGES.includes(route.name);

	const isEnrolledForProjectSettings =
		isProjectSettingsPage && canShowExperience && canInvitePeopleToProject;

	const hasLoadedRemoteState = useRef(false);
	const [{ location }] = useRouter();

	useEffect(() => {
		if (projectContextData?.projectKey && projectContextData?.projectType === CORE_PROJECT) {
			fetchIsSampleProject(projectContextData.projectKey);
		}
	}, [fetchIsSampleProject, projectContextData?.projectKey, projectContextData?.projectType]);

	useEffect(() => {
		if (projectContextData) {
			const {
				projectKey,
				projectName,
				projectId,
				simplified: isNextGen,
				projectType,
				isProjectAdmin: isProjectAdminMaybe,
			} = projectContextData;

			setProjectState({
				canInvitePeopleToProject,
				projectKey,
				projectId,
				projectName,
				isNextGen,
				projectType,
				isProjectAdmin: Boolean(isProjectAdminMaybe),
				userRole: getUserRole(tenantContext.isSiteAdmin, tenantContext.isAdmin),
				edition: getEditionForProject(projectType, tenantContext.appEditions),
				isSampleProject: isSampleProject[projectKey],
				isTrelloCrossflow,
			});
		}
		if (fg('quickstart_onboarding_in_jira_plans')) {
			setContainerType(containerType);
		}
	}, [
		setProjectState,
		projectContextData,
		canInvitePeopleToProject,
		isProjectSettingsPage,
		tenantContext,
		isSampleProject,
		isTrelloCrossflow,
		containerType,
		setContainerType,
	]);

	useEffect(() => {
		fetchOnboardingType(projectContextData?.projectId, projectContextData?.projectType);
	}, [fetchOnboardingType, projectContextData]);

	// When the component is re-mounted after the initial remote state load.
	useEffect(() => {
		// We can show the experience and are in the right page or experiment.
		const pathNameWithoutTrailingSlash = extractTrailingSlashFromPath(location.pathname);

		const isJsmBusinessPeopleSettingsPage =
			projectContextData?.projectType === SERVICE_DESK_PROJECT &&
			jsmOnboardingData?.onboardingType === BUSINESS &&
			pathNameWithoutTrailingSlash.endsWith(PEOPLE_SETTINGS_URL);

		const canReallyShowExperience =
			canShowExperience &&
			// TODO: move this logic inside the useQuickstart hook (canShowExperience)
			(isJsmBusinessPeopleSettingsPage || !isProjectSettingsPage || canInvitePeopleToProject);

		if (!canReallyShowExperience) {
			return;
		}

		const projectType = projectContextData?.projectType;

		if (!isQuickstartLoaded && !hasLoadedRemoteState.current) {
			if (
				quickstartStatus === QUICKSTART_UI_STATUS.MINIMIZED ||
				quickstartStatus === QUICKSTART_UI_STATUS.DISMISSED
			) {
				syncVisibilityWithLocal();
				return;
			}
			// We need to load QuickStart remote data.
			hasLoadedRemoteState.current = true;
			const { atlassianAccountId, cloudId, appEditions, isAdmin } = tenantContext;
			refreshAppState({
				atlassianAccountId,
				cloudId,
				appEditions,
				isAdmin,
				createAnalyticsEvent,
				canEditPlans,
				isAdvancedRoadmapsTrial,
				isTargetedForReverseTrials,
				projectType,
				containerType,
			});
			return;
		}

		if (!shouldRenderComponent && isQuickstartLoaded) {
			// Quickstart has loaded data but currently hidden.
			setShouldRenderComponent(true);
		}
	}, [
		refreshAppState,
		canInvitePeopleToProject,
		canShowExperience,
		isProjectSettingsPage,
		isQuickstartLoaded,
		setShouldRenderComponent,
		shouldRenderComponent,
		tenantContext,
		location.pathname,
		projectContextData,
		jsmOnboardingData,
		createAnalyticsEvent,
		canEditPlans,
		isAdvancedRoadmapsTrial,
		isTargetedForReverseTrials,
		quickstartStatus,
		syncVisibilityWithLocal,
		containerType,
	]);

	/**
		Note: We triggers feature exposure for our
		Invite people to project experiment,
		If user is in project settings page.
		FD Ticket: https://hello.atlassian.net/browse/FD-15096
	*/
	useEffect(
		() =>
			// When component unmounts we should set shouldRenderComponent to false
			() => {
				// NOTE: we need to make sure the nudge is not active when the component unmounts.
				// This prevents seeing the nudge flickering if a user navigates when the nudge
				// is active for example.
				forceHideNudge();
				setShouldRenderComponent(false);
			},
		[setShouldRenderComponent, forceHideNudge],
	);

	useEffect(() => {
		if (
			isQuickstartLoaded === true &&
			quickstartStatus === QUICKSTART_UI_STATUS.OPEN &&
			hasOpened === false
		) {
			setHasOpened();
		}
	}, [isQuickstartLoaded, quickstartStatus, setHasOpened, hasOpened]);

	return {
		canShowExperience,
		isEnrolledForProjectSettings,
	};
};

export const useQuickstartInitialiseWithPersonalisation = (
	projectContextData: ProjectContextType | null,
	containerType: 'project' | 'plan' = 'project',
) => {
	const tenantContext = useTenantContext();
	const isTrelloCrossflow = useIsTrelloCrossflowEnabled();

	const canShowExperience = useQuickstart(projectContextData);
	const [quickstartStatus] = useQuickstartUiStatus();
	const [{ data: jsmOnboardingData }, { fetchOnboardingType }] = useJsmOnboardingType();
	useRefreshQuickstartOnProjectSwitch(projectContextData);
	const convertToRootTaskList = useConvertToRootTaskList();

	const canEditPlans = useNavigationEditPlansPermission();
	const isAdvancedRoadmapsTrial = useIsAdvancedRoadmapsTrial();
	const isTargetedForReverseTrials = useIsTargetedForReverseTrials();
	const [
		{ isQuickstartLoaded, shouldRenderComponent },
		{
			refreshAppState,
			refreshAppStateUiStatusOnly,
			setShouldRenderComponent,
			forceHideNudge,
			setProjectState,
			setContainerType,
		},
	] = useJswOnboarding();
	const isEligibleForJtbdOnboarding = useCommonEligibilityConditionsForJtbdOnboarding();

	// TODO: Can share this variable with rest of file
	const projectTypeForTO = fg('tailored_onboarding_m3_gate')
		? projectContextData?.projectType
		: CORE_PROJECT;

	const { isReady: personalisationDataIsReady, data: personalisationData } =
		useTailoredOnboardingPersonalisationM2({
			isEligible: isEligibleForJtbdOnboarding,
			additionalParameters: { projectType: projectTypeForTO },
		});
	const [{ hasOpened }, { setHasOpened }] = useHasOpened();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isSampleProject, { fetchIsSampleProject }] = useIsSampleProjectService();

	const canInvitePeopleToProject = getCanInvitePeopleToProject(projectContextData, tenantContext);
	const route = useCurrentRoute();
	const isProjectSettingsPage = PROJECT_SETTING_PAGES.includes(route.name);

	const isEnrolledForProjectSettings =
		isProjectSettingsPage && canShowExperience && canInvitePeopleToProject;

	const hasLoadedRemoteState = useRef(false);
	const [{ location }] = useRouter();

	useEffect(() => {
		if (projectContextData?.projectKey && projectContextData?.projectType === CORE_PROJECT) {
			fetchIsSampleProject(projectContextData.projectKey);
		}
	}, [fetchIsSampleProject, projectContextData?.projectKey, projectContextData?.projectType]);

	useEffect(() => {
		if (projectContextData) {
			const {
				projectKey,
				projectName,
				projectId,
				simplified: isNextGen,
				projectType,
				isProjectAdmin: isProjectAdminMaybe,
			} = projectContextData;

			setProjectState({
				canInvitePeopleToProject,
				projectKey,
				projectId,
				projectName,
				isNextGen,
				projectType,
				isProjectAdmin: Boolean(isProjectAdminMaybe),
				userRole: getUserRole(tenantContext.isSiteAdmin, tenantContext.isAdmin),
				edition: getEditionForProject(projectType, tenantContext.appEditions),
				isSampleProject: isSampleProject[projectKey],
				isTrelloCrossflow,
			});
		}
		if (fg('quickstart_onboarding_in_jira_plans')) {
			setContainerType(containerType);
		}
	}, [
		setProjectState,
		projectContextData,
		canInvitePeopleToProject,
		isProjectSettingsPage,
		tenantContext,
		isSampleProject,
		isTrelloCrossflow,
		containerType,
		setContainerType,
	]);

	useEffect(() => {
		fetchOnboardingType(projectContextData?.projectId, projectContextData?.projectType);
	}, [fetchOnboardingType, projectContextData]);

	// When the component is re-mounted after the initial remote state load.
	useEffect(() => {
		// We can show the experience and are in the right page or experiment.
		const pathNameWithoutTrailingSlash = extractTrailingSlashFromPath(location.pathname);

		const isJsmBusinessPeopleSettingsPage =
			projectContextData?.projectType === SERVICE_DESK_PROJECT &&
			jsmOnboardingData?.onboardingType === BUSINESS &&
			pathNameWithoutTrailingSlash.endsWith(PEOPLE_SETTINGS_URL);

		const canReallyShowExperience =
			canShowExperience &&
			// TODO: move this logic inside the useQuickstart hook (canShowExperience)
			(isJsmBusinessPeopleSettingsPage || !isProjectSettingsPage || canInvitePeopleToProject);

		if (!canReallyShowExperience) {
			return;
		}

		const projectType = projectContextData?.projectType;

		if (personalisationDataIsReady && !isQuickstartLoaded && !hasLoadedRemoteState.current) {
			// We need to load QuickStart remote data.
			hasLoadedRemoteState.current = true;
			if (
				personalisationData.QUICKSTART &&
				personalisationData.QUICKSTART?.length > 0 &&
				// For Tailored onboarding M3, we have an option to stick to the FE and avoid the BE since M2 scoped the BE call to business projects
				projectType === CORE_PROJECT
			) {
				const taskList = convertToRootTaskList(personalisationData.QUICKSTART);
				refreshAppStateUiStatusOnly({ taskList, containerType });
			} else {
				const { atlassianAccountId, cloudId, appEditions, isAdmin } = tenantContext;
				refreshAppState({
					atlassianAccountId,
					cloudId,
					appEditions,
					isAdmin,
					createAnalyticsEvent,
					canEditPlans,
					isAdvancedRoadmapsTrial,
					isTargetedForReverseTrials,
					projectType,
					containerType,
				});
			}
			return;
		}

		if (!shouldRenderComponent && isQuickstartLoaded) {
			// Quickstart has loaded data but currently hidden.
			setShouldRenderComponent(true);
		}
	}, [
		refreshAppState,
		canInvitePeopleToProject,
		canShowExperience,
		isProjectSettingsPage,
		isQuickstartLoaded,
		refreshAppStateUiStatusOnly,
		createAnalyticsEvent,
		canEditPlans,
		isAdvancedRoadmapsTrial,
		isTargetedForReverseTrials,
		location.pathname,
		projectContextData?.projectType,
		jsmOnboardingData?.onboardingType,
		personalisationDataIsReady,
		shouldRenderComponent,
		personalisationData.QUICKSTART,
		tenantContext,
		setShouldRenderComponent,
		convertToRootTaskList,
		containerType,
	]);

	/**
		Note: We triggers feature exposure for our
		Invite people to project experiment,
		If user is in project settings page.
		FD Ticket: https://hello.atlassian.net/browse/FD-15096
	*/
	useEffect(
		() =>
			// When component unmounts we should set shouldRenderComponent to false
			() => {
				// NOTE: we need to make sure the nudge is not active when the component unmounts.
				// This prevents seeing the nudge flickering if a user navigates when the nudge
				// is active for example.
				forceHideNudge();
				setShouldRenderComponent(false);
			},
		[setShouldRenderComponent, forceHideNudge],
	);

	useEffect(() => {
		if (
			isQuickstartLoaded === true &&
			quickstartStatus === QUICKSTART_UI_STATUS.OPEN &&
			hasOpened === false
		) {
			setHasOpened();
		}
	}, [isQuickstartLoaded, quickstartStatus, setHasOpened, hasOpened]);

	return {
		canShowExperience,
		isEnrolledForProjectSettings,
	};
};
